import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IDossiePFResponse, IDossiePJResponse, IConsultarDossieOptions, ISituacaoCadastralPF, ISituacaoCadastralPJ } from '../../models/dossie';

export interface IComplianceAlert {
  open: boolean;
  menorIdade: boolean;
  pessoaRestrita: boolean;
  nome: string;
}

export interface ICobrancaAlert {
  open: boolean;
  documento: string;
  idVeles: number;
  nome?: string;
  options?: IConsultarDossieOptions;
  origemConsulta?: IOrigemConsulta | null;
}

export interface IOrigemConsulta {
  documento: string;
  nome?: string;
  tipo: 'CPF' | 'CNPJ';
  idVeles?: number;
}

export type IModoVisualizacaoCadastral = 'online' | 'estatico';

interface IDossieState {
  currentResult: (IDossiePFResponse | IDossiePJResponse) | null;
  situacaoCadastralOriginal: (ISituacaoCadastralPF | ISituacaoCadastralPJ) | null;
  situacaoCadastralOnline: (ISituacaoCadastralPF | ISituacaoCadastralPJ) | null;
  modoVisualizacaoCadastral: IModoVisualizacaoCadastral;
  error: string | null;
  complianceAlert: IComplianceAlert | null;
  cobrancaAlert: ICobrancaAlert | null;
  origemConsulta: IOrigemConsulta | null;
  origemMinimizada: boolean;
}

const initialState: IDossieState = {
  currentResult: null,
  situacaoCadastralOriginal: null,
  situacaoCadastralOnline: null,
  modoVisualizacaoCadastral: 'online',
  error: null,
  complianceAlert: null,
  cobrancaAlert: null,
  origemConsulta: null,
  origemMinimizada: false,
};

const dossieSlice = createSlice({
  name: 'dossie',
  initialState,
  reducers: {
    setDossieResult(state, action: PayloadAction<IDossiePFResponse | IDossiePJResponse | null>) {
      state.currentResult = action.payload;
      state.situacaoCadastralOriginal = action.payload?.situacaoCadastral || null;
      state.situacaoCadastralOnline = null;
      state.modoVisualizacaoCadastral = 'online';
      state.error = null;
    },
    setDossieError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setComplianceAlert(state, action: PayloadAction<IComplianceAlert | null>) {
      state.complianceAlert = action.payload;
    },
    clearComplianceAlert(state) {
      state.complianceAlert = null;
    },
    setCobrancaAlert(state, action: PayloadAction<ICobrancaAlert | null>) {
      state.cobrancaAlert = action.payload;
    },
    clearCobrancaAlert(state) {
      state.cobrancaAlert = null;
    },
    setOrigemConsulta(state, action: PayloadAction<IOrigemConsulta | null>) {
      state.origemConsulta = action.payload;
    },
    setOrigemMinimizada(state, action: PayloadAction<boolean>) {
      state.origemMinimizada = action.payload;
    },
    clearOrigemConsulta(state) {
      state.origemConsulta = null;
      state.origemMinimizada = false;
    },
    setModoVisualizacaoCadastral(state, action: PayloadAction<IModoVisualizacaoCadastral>) {
      state.modoVisualizacaoCadastral = action.payload;
    },
    updateSituacaoCadastral(state, action: PayloadAction<ISituacaoCadastralPF | ISituacaoCadastralPJ>) {
      state.situacaoCadastralOnline = action.payload;
      state.modoVisualizacaoCadastral = 'online';
      if (state.currentResult) {
        state.currentResult.situacaoCadastral = action.payload;
      }
    },
    resetDossie(state) {
      state.currentResult = null;
      state.situacaoCadastralOriginal = null;
      state.situacaoCadastralOnline = null;
      state.modoVisualizacaoCadastral = 'online';
      state.error = null;
      state.complianceAlert = null;
      state.cobrancaAlert = null;
      state.origemConsulta = null;
      state.origemMinimizada = false;
    }
  },
});

export const {
  setDossieResult,
  setDossieError,
  setComplianceAlert,
  clearComplianceAlert,
  setCobrancaAlert,
  clearCobrancaAlert,
  setOrigemConsulta,
  setOrigemMinimizada,
  clearOrigemConsulta,
  setModoVisualizacaoCadastral,
  updateSituacaoCadastral,
  resetDossie
} = dossieSlice.actions;
export const dossieReducer = dossieSlice.reducer;

